import { useLayoutEffect, useRef, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { reserveEmphasisWidth } from "@/lib/reserve-emphasis-width";
import { InfoTooltip } from "./InfoTooltip";

// Ruhezustand des Badges. `active` (Toggle-Zustand) überschreibt den Ton mit Accent, damit
// Callsites nicht `tone={x ? "accent" : "neutral"}` und `active={x}` doppelt führen müssen.
// Bewusst kein Danger-Ton: Rot ist laut UI_CONVENTIONS eine Button-Farbe fuer destruktive
// Aktionen, keine Hervorhebung fuer Zustaende. Ein verfehltes Kriterium wird ueber Glyph und
// Text ausgewiesen, nicht ueber Farbe.
export type KpiBadgeTone = "neutral" | "muted";

const TONE_CLASSES: Record<KpiBadgeTone | "active", { rest: string; hover: string }> = {
  // bg-accent (statt Inline-Style) rebindet --context-ink auf on-accent, dadurch invertieren
  // text-ink/N-Kinder wie das (i)-Icon automatisch. Siehe UI_CONVENTIONS "How text-ink/N adapts".
  active: { rest: "bg-accent font-semibold", hover: "hover:opacity-100" },
  neutral: { rest: "bg-ink/[0.06] text-ink", hover: "hover:text-ink" },
  muted: { rest: "bg-ink/[0.02] text-ink-muted", hover: "hover:text-ink" },
};

/**
 * Kompaktes KPI-Badge einer Listenzeile: Label + Wert in einer Pille, optional klickbar
 * (Tab-Switch) und optional mit angehängtem (i)-Tooltip. EIN Ort für Größe, Pill-Form und
 * Farbton, damit Zeilen-KPIs nicht pro Feature neu erfunden werden.
 *
 * Der Tooltip sitzt bewusst als eigener Button neben dem Label-Button, nicht darin: ein
 * Button in einem Button ist ungültiges HTML und macht das Label per Tastatur unerreichbar.
 */
export function KpiBadge({
  id,
  tone = "neutral",
  active,
  expanded,
  onClick,
  title,
  info,
  children,
}: {
  // Testanker; der angehängte ⓘ-Auslöser leitet `${id}-info` davon ab.
  id?: string;
  tone?: KpiBadgeTone;
  // Opt-in Toggle-Semantik: setzt aria-pressed, damit der aktive Tab auch für Screenreader und
  // im forced-colors-Modus erkennbar ist (der Accent-Zustand allein reicht dort nicht).
  active?: boolean;
  /**
   * Set instead of relying on `active` when the badge OPENS A REGION rather than switching a tab.
   * `aria-pressed` says "this button is on", `aria-expanded` says "the thing it controls is
   * showing" — a screen reader announces them differently, and a disclosure trigger that claims to
   * be a toggle button is simply the wrong description. Both may be set: the company ranking row
   * uses `active` for the accent tone and `expanded` for what it actually does.
   */
  expanded?: boolean;
  onClick?: (event: ReactMouseEvent) => void;
  title?: string;
  info?: { title: string; ariaLabel: string; width?: string; content: ReactNode };
  children: ReactNode;
}) {
  const labelRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (active === undefined || !labelRef.current) return;
    const label = labelRef.current;
    const measure = () => reserveEmphasisWidth(label);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(label);
    return () => observer.disconnect();
  }, [active, children]);
  const label = <span ref={labelRef} className="smycles-emphasis-label">{children}</span>;
  const { rest, hover } = TONE_CLASSES[active ? "active" : tone];
  return (
    <span
      id={id}
      className={`inline-flex items-center gap-1.5 rounded-full py-1.5 text-smycles-meta tabular-nums ${
        info ? "pl-3 pr-2" : "px-3"
      } ${rest} ${active === false ? "font-normal" : ""}`}
      title={onClick ? undefined : title}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          title={title}
          aria-pressed={expanded === undefined ? active : undefined}
          aria-expanded={expanded}
          className={`inline-flex items-center gap-1 rounded-full outline-none transition focus-visible:ring-2 focus-visible:ring-ink-accent-ui ${hover}`}
        >
          {label}
        </button>
      ) : (
        label
      )}
      {info ? (
        <span onClick={(event) => event.stopPropagation()} className="inline-flex">
          <InfoTooltip
            id={id && `${id}-info`}
            ariaLabel={info.ariaLabel}
            title={info.title}
            placement="bottom"
            className="text-left"
            contentClassName={info.width ?? "w-[22rem] max-w-[22rem]"}
          >
            {info.content}
          </InfoTooltip>
        </span>
      ) : null}
    </span>
  );
}
